import React, { useRef, useState, useEffect } from 'react';
import './VoicePlayer.css';

const VoicePlayer = ({ url, duration, waveform = [] }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setAudioDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;

    const bounds = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - bounds.left;
    const percentage = x / bounds.width;
    audio.currentTime = percentage * audioDuration;
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  return (
    <div className="voice-player">
      <audio ref={audioRef} src={url} preload="metadata" />
      
      <button className="voice-play-btn" onClick={togglePlayPause}>
        {isPlaying ? '⏸' : '▶️'}
      </button>
      
      <div className="voice-player-content">
        <div className="voice-waveform" onClick={handleSeek}>
          {waveform && waveform.length > 0 ? (
            <div className="waveform-bars">
              {waveform.map((amplitude, index) => (
                <div
                  key={index}
                  className="waveform-bar"
                  style={{
                    height: `${amplitude * 100}%`,
                    opacity: (index / waveform.length) * 100 < progress ? 1 : 0.3
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="voice-progress-bar">
              <div className="voice-progress-fill" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
        
        <div className="voice-time">
          <span>{formatTime(currentTime)}</span>
          <span className="voice-time-separator">/</span>
          <span>{formatTime(audioDuration)}</span>
        </div>
      </div>
    </div>
  );
};

export default VoicePlayer;
